import { useEffect, useMemo } from 'react';
import { useWidgetStore } from '../../hooks';
import { useWidgetConfig } from '../../context';
import { ReleaseNoteCard } from './ReleaseNoteCard';

/**
 * Renders a chronologically sorted (newest first) list of release notes.
 * Marks unseen entries with a "New" badge and auto-marks all as seen
 * when the tab is viewed.
 */
export function WhatsNewTab() {
  const config = useWidgetConfig();
  const seenReleases = useWidgetStore((s) => s.seenReleases);
  const markReleasesSeen = useWidgetStore((s) => s.markReleasesSeen);

  const releases = config.releases ?? [];

  // Sort by date descending
  const sorted = useMemo(
    () =>
      [...releases].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      ),
    [releases],
  );

  // Mark all as seen when this tab is mounted
  useEffect(() => {
    const unseenIds = sorted
      .filter((r) => !seenReleases.has(r.id))
      .map((r) => r.id);
    if (unseenIds.length > 0) {
      markReleasesSeen(unseenIds);
    }
  }, [sorted, seenReleases, markReleasesSeen]);

  if (sorted.length === 0) {
    return (
      <div
        style={{
          padding: '48px 24px',
          textAlign: 'center',
          color: 'var(--sw-text-secondary, #9a9ab0)',
        }}
      >
        <MegaphoneEmpty />
        <p style={{ marginTop: '12px', fontSize: '14px' }}>
          No release notes yet
        </p>
        <p style={{ marginTop: '4px', fontSize: '12px' }}>
          Check back soon for product updates.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 16px' }} role="feed" aria-label="Release notes">
      {sorted.map((note) => (
        <ReleaseNoteCard
          key={note.id}
          note={note}
          isNew={!seenReleases.has(note.id)}
        />
      ))}
    </div>
  );
}

/** Empty-state icon */
function MegaphoneEmpty() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ margin: '0 auto', opacity: 0.5 }}
      aria-hidden="true"
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}
